import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { SwitchButton } from 'components-ref/atoms'
import { useModalContext } from 'context/modalContext'
import { createMediaCategories } from 'services/endpoints/mediaCategory'
import './_add-category-modal.scss'
// import { getProfile } from 'services/endpoints/profile'
import { useProfileCacheStore } from 'store/profileCacheStore'

interface Props {
  onClose?: () => void
  existingNamesArray?: string[]
}

export const AddCategoryModal = ({ onClose, existingNamesArray }: Props) => {
  const [categoryName, setCategoryName] = useState<string>('')
  const [isPublic, setIsPublic] = useState(false)
  const [isSuggestionsAlgo, setIsSuggestionsAlgo] = useState(true)

  const modalData = useModalContext()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const [errorMessage, setErrorMessage] = useState('')

  const createCategory = useMutation(
    async () => await createMediaCategories(categoryName, isPublic, isSuggestionsAlgo),
    {
onSuccess: (resp: any) => {
    const rawCreated = resp?.data

    if (!rawCreated?.id) {
        console.error(
            '[AddCategoryModal] Invalid create response:',
            resp
        )
        return
    }

    const created = {
        ...rawCreated,
        posts: rawCreated.posts ?? 0,
        premium: rawCreated.premium ?? 0,
        stories: rawCreated.stories ?? 0,
        messages: rawCreated.messages ?? 0
    }

    /**
     * Update every parameterized media-category query:
     *
     * [
     *   'allMediaCategories',
     *   search,
     *   sortType,
     *   sortOrder
     * ]
     */
    const mediaCategoryQueries = queryClient
        .getQueryCache()
        .findAll({
            predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === 'allMediaCategories'
        })

    mediaCategoryQueries.forEach(query => {
        const queryKey = query.queryKey as [
            string,
            string,
            'name' | 'date_created' | 'posts' | 'premium',
            'asc' | 'desc'
        ]

        const [, searchTerm, sortType, sortOrder] = queryKey

        queryClient.setQueryData(
            queryKey,
            (old: any) => {
                if (!old || !Array.isArray(old.data)) {
                    return old
                }

                if (
                    old.data.some(
                        (category: any) =>
                            category.id === created.id
                    )
                ) {
                    return old
                }

                const normalizedSearch =
                    searchTerm?.trim().toLowerCase() ?? ''

                // Do not insert into cached search results
                // where the category name does not match.
                if (
                    normalizedSearch &&
                    !created.name
                        .toLowerCase()
                        .includes(normalizedSearch)
                ) {
                    return old
                }

                const nextCategories = [
                    ...old.data,
                    created
                ]

                nextCategories.sort(
                    (a: any, b: any) => {
                        let result = 0

                        switch (sortType) {
                            case 'name':
                                result = a.name.localeCompare(
                                    b.name
                                )
                                break

                            case 'posts':
                                result =
                                    (a.posts ?? 0) -
                                    (b.posts ?? 0)
                                break

                            case 'premium':
                                result =
                                    (a.premium ?? 0) -
                                    (b.premium ?? 0)
                                break

                            case 'date_created':
                            default:
                                /**
                                 * The create response does not
                                 * contain created_at.
                                 *
                                 * Treat the newly created category
                                 * as the newest item.
                                 */
                                if (a.id === created.id) {
                                    result = 1
                                } else if (
                                    b.id === created.id
                                ) {
                                    result = -1
                                } else {
                                    result =
                                        new Date(
                                            a.created_at ?? 0
                                        ).getTime() -
                                        new Date(
                                            b.created_at ?? 0
                                        ).getTime()
                                }

                                break
                        }

                        return sortOrder === 'asc'
                            ? result
                            : -result
                    }
                )

                return {
                    ...old,
                    data: nextCategories
                }
            }
        )
    })

    /**
     * Update React Query profile caches containing
     * all_media_categories.
     */
    queryClient.setQueriesData(
        {
            predicate: query => {
                const cachedData: any =
                    query.state.data

                const profile =
                    cachedData?.data ?? cachedData

                return Array.isArray(
                    profile?.all_media_categories
                )
            }
        },
        (old: any) => {
            if (!old) {
                return old
            }

            const profile = old?.data ?? old

            const previousCategories =
                Array.isArray(
                    profile?.all_media_categories
                )
                    ? profile.all_media_categories
                    : []

            if (
                previousCategories.some(
                    (category: any) =>
                        category.id === created.id
                )
            ) {
                return old
            }

            const updatedProfile = {
                ...profile,
                all_media_categories: [
                    ...previousCategories,
                    created
                ]
            }

            return old?.data
                ? {
                      ...old,
                      data: updatedProfile
                  }
                : updatedProfile
        }
    )

    /**
     * Update Zustand profiles.
     *
     * Do not compare created.user_id because the create
     * response does not contain user_id.
     */
    try {
        const store =
            useProfileCacheStore.getState()

        Object.entries(store.cache).forEach(
            ([key, cachedValue]) => {
                const profile = cachedValue as any

                if (
                    !Array.isArray(
                        profile?.all_media_categories
                    )
                ) {
                    return
                }

                if (
                    profile.all_media_categories.some(
                        (category: any) =>
                            category.id === created.id
                    )
                ) {
                    return
                }

                const [
                    usernameFromKey,
                    viewerFromKey
                ] = key.split('::')

                const viewerId = viewerFromKey

                store.setProfile(
                    profile.username ??
                        usernameFromKey,
                    {
                        ...profile,
                        all_media_categories: [
                            ...profile.all_media_categories,
                            created
                        ]
                    },
                    viewerId
                )
            }
        )
    } catch (error) {
        console.warn(
            '[AddCategoryModal] Failed to patch Zustand profile cache:',
            error
        )
    }

    onClose
        ? onClose()
        : modalData.clearModal()
},
onError: (error: any) => {
  const message =
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    t('error:anErrorOccuredPleaseTryAgain')

  setErrorMessage(message)
}
    }
  )

  const createCategorySubmit = () => {
    setErrorMessage('')

    const trimmed = categoryName.trim()

    if (trimmed === '') {
      setErrorMessage(t('pleaseInputCategoryName'))
      return
    }

    if (existingNamesArray?.includes(trimmed)) {
      setErrorMessage(t('categoryNameAlreadyExists'))
      return
    }

    createCategory.mutate()
  }

    return (
        <div>
            <InputCard
                customClass='add-category-modal-input'
                hasDesc={true}
                desc={`${categoryName.trim().length} / 30 ${t('characters')}`}
                maxChars={30}
                type='text'
                value={categoryName}
                changeFn={(value: any) => {
                    setCategoryName(value)
                }}
                label={t('categoryName')}
            />
            <div className="switch-group-container">
            <div className="switch-item-1">
                <SwitchButton active={isPublic} toggle={() => setIsPublic(!isPublic)} />
                <span className="switch-label">{isPublic ? "Appear On Feed" : "Non Public"}</span>
            </div>
{/*             <div className="switch-item-2">
                <SwitchButton active={isSuggestionsAlgo} toggle={() => setIsSuggestionsAlgo(!isSuggestionsAlgo)} />
                <span className="switch-label">
                Sales Tracking - Chat
                </span>
            </div> */}
            </div>
            {errorMessage && (
              <div className='add-category-modal-error'>
                {errorMessage}
              </div>
            )}
            <div className='category-modal-buttons-container'>
                <Button
                    text={t('back')}
                    color='black'
                    clickFn={() => {
                        onClose ? onClose() : modalData.clearModal()
                        setCategoryName('')
                    }}
                    height='3'
                    width='13'
                    font='mont-14-normal'
                />
                <Button
                    text={t('add')}
                    color='blue'
                    customClass={`add-category-modal-button ${
                        createCategory.isLoading
                            ? 'add-category-modal-button-loading'
                            : ''
                    }`}
                    clickFn={createCategorySubmit}
                    height='3'
                    width='13'
                    font='mont-14-normal'
                />
            </div>
        </div>
    )
}
